import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";

const LINKS = [
    { label: "Equipo", href: "#equipo" },
    { label: "Resultados", href: "#resultados" },
    { label: "Cómo funciona", href: "#como-funciona" },
    { label: "Contacto", href: "#contacto" },
];

export const Navbar = ({ onBook }) => {
    const { user } = useAuth();
    const nav = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        let ticking = false;
        const onScroll = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
                setScrolled(window.scrollY > 30);
                ticking = false;
            });
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const dashPath = user && user.role === "super_admin" ? "/admin" : "/dashboard";

    return (
        <nav
            data-testid="navbar"
            className={`fixed left-1/2 top-4 z-50 w-[94%] max-w-6xl -translate-x-1/2 transform-gpu will-change-transform rounded-full border px-5 py-3 transition-all duration-300 ${
                scrolled
                    ? "border-white/10 bg-black/70 backdrop-blur-lg"
                    : "border-white/5 bg-black/30 backdrop-blur-md"
            }`}
        >
            <div className="flex items-center justify-between">
                <Link to="/" data-testid="logo" className="flex items-center gap-2.5">
                    <img src="/av-logo.png" alt="AVENDOR" className="h-6 w-auto select-none" />
                    <span className="text-lg font-bold tracking-tight text-foreground">
                        AVENDOR
                    </span>
                    <span className="hidden rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.2em] text-primary sm:inline">
                        Texas Team
                    </span>
                </Link>

                <div className="hidden items-center gap-8 md:flex">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-foreground"
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                <div className="flex items-center gap-2">
                    {user ? (
                        <button
                            data-testid="nav-dashboard-btn"
                            onClick={() => nav(dashPath)}
                            className="rounded-full bg-nav-button px-5 py-2 text-xs font-semibold uppercase tracking-widest text-foreground transition-all hover:bg-nav-button/70 active:scale-95"
                        >
                            Panel
                        </button>
                    ) : (
                        <Link
                            to="/login"
                            data-testid="nav-login-btn"
                            className="hidden rounded-full bg-nav-button px-5 py-2 text-xs font-semibold uppercase tracking-widest text-foreground transition-all hover:bg-nav-button/70 active:scale-95 sm:inline-block"
                        >
                            Ingresar
                        </Link>
                    )}
                    <button
                        data-testid="nav-book-btn"
                        onClick={onBook}
                        className="rounded-full bg-primary px-5 py-2 text-xs font-bold uppercase tracking-widest text-primary-foreground shadow-[0_0_18px_hsl(119_99%_46%/0.35)] transition-all hover:brightness-110 active:scale-95"
                    >
                        Agendar
                    </button>
                    <button
                        className="text-foreground md:hidden"
                        onClick={() => setOpen((o) => !o)}
                        data-testid="nav-menu-toggle"
                        aria-label="Menú"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="mt-3 flex flex-col gap-1 border-t border-white/10 pt-3 md:hidden">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            onClick={() => setOpen(false)}
                            className="rounded-lg px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
                        >
                            {l.label}
                        </a>
                    ))}
                    {!user && (
                        <Link
                            to="/login"
                            onClick={() => setOpen(false)}
                            className="rounded-lg px-3 py-2 text-sm font-medium text-primary"
                        >
                            Ingresar
                        </Link>
                    )}
                </div>
            )}
        </nav>
    );
};
